import { inject, injectable } from "inversify";
import { ApiCallService } from "./apiCall.service";
import axios from "axios";
import {
  Certification,
  DashboardInterface,
  InviteUserInterface,
  InvitedUserInterface,
  ParticipantDashboard,
  ParticipantInterface,
  UserFilterInterface,
  UserInterface,
} from "../interface/UserInterface";
import { AuthService } from "./authService";
import { PagingInterface } from "../interface/PaginationInterface";

@injectable()
export class UserService {
  constructor(
    @inject(ApiCallService) public apiCallService: ApiCallService,
    @inject(AuthService) public authService: AuthService,
  ) {}

  public async updateProfile(updatedData: {
    first_name: string;
    last_name: string;
    email: string;
  }): Promise<UserInterface | void> {
    return axios
      .put<UserInterface>(this.apiCallService.apiUrl("users"), updatedData)
      .then(() => this.authService.fetchUser());
  }

  public async updatePassword(
    oldPassword: string,
    newPassword: string,
  ): Promise<UserInterface> {
    return axios
      .put<UserInterface>(this.apiCallService.apiUrl("users/password"), {
        old_password: oldPassword,
        password: newPassword,
      })
      .then((result) => result.data);
  }

  public async getInvitedUser(
    userFilters: Partial<UserFilterInterface>,
    pageConfig: { page?: number; offset?: number } = {
      page: 1,
      offset: 8,
    },
  ): Promise<PagingInterface<InvitedUserInterface>> {
    return axios
      .get<
        PagingInterface<InvitedUserInterface>
      >(this.apiCallService.apiUrl("users/invitations", { ...userFilters, ...pageConfig }))
      .then((result) => result.data);
  }

  public async getActiveParticipants(): Promise<InvitedUserInterface[]> {
    return axios
      .get<
        InvitedUserInterface[]
      >(this.apiCallService.apiUrl("users/invitations/active-participants"))
      .then((result) => result.data);
  }

  public async getRecentlyCompleted(): Promise<InvitedUserInterface[]> {
    return axios
      .get<
        InvitedUserInterface[]
      >(this.apiCallService.apiUrl("users/invitations/recently-completed"))
      .then((result) => result.data);
  }

  public async getRecentlyCompletedCount(): Promise<number> {
    return axios
      .get<{
        count: number;
      }>(
        this.apiCallService.apiUrl(
          "users/invitations/recently-completed/count",
        ),
      )
      .then((result) => result.data.count);
  }

  public async addUser(
    userData: InviteUserInterface,
  ): Promise<InvitedUserInterface> {
    return axios
      .post<InvitedUserInterface>(
        this.apiCallService.apiUrl("users/invitations"),
        {
          ...userData,
        },
      )
      .then((result) => result.data);
  }

  public async getUserWithRole(
    role: string,
    all: boolean = false,
    pageConfig: { page?: number; offset?: number } = {
      page: 1,
      offset: 8,
    },
    email: string = "",
    signal?: AbortSignal,
  ): Promise<PagingInterface<UserInterface>> {
    return axios
      .get<PagingInterface<UserInterface>>(
        this.apiCallService.apiUrl("users/index"),
        {
          signal,
          params: {
            role,
            all,
            pageConfig,
            email,
          },
        },
      )
      .then((result) => result.data);
  }
  public async sendInvite(id: number): Promise<{ status: "ok" }> {
    return axios
      .post<{
        status: "ok";
      }>(this.apiCallService.apiUrl(`users/invitations/${id}/send`))
      .then((result) => result.data);
  }
  public async deleteInvitation(id: number): Promise<{ status: string }> {
    return axios
      .delete<{
        status: string;
      }>(this.apiCallService.apiUrl(`users/invitations/${id}`))
      .then((result) => result.data);
  }

  public async editInvitation(
    user: InviteUserInterface,
    id: number,
  ): Promise<InvitedUserInterface> {
    return axios
      .put(this.apiCallService.apiUrl(`users/invitations/${id}`), {
        ...user,
      })
      .then((result) => result.data);
  }

  public async feedbackDurationleft(id: number): Promise<ParticipantInterface> {
    return axios
      .get<ParticipantInterface>(
        this.apiCallService.apiUrl(`users/invitations/${id}/dashboard`),
      )
      .then((result) => result.data);
  }

  public async getCurrentInvitation(id: number): Promise<InvitedUserInterface> {
    return axios
      .get<InvitedUserInterface>(
        this.apiCallService.apiUrl(`users/invitations/${id}`),
      )
      .then((result) => result.data);
  }

  public async getDashboardData(): Promise<DashboardInterface> {
    return axios
      .get<DashboardInterface>(this.apiCallService.apiUrl(`dashboard`))
      .then((result) => result.data);
  }

  public async finsihFeedback(id: number): Promise<InvitedUserInterface> {
    return axios
      .post<InvitedUserInterface>(
        this.apiCallService.apiUrl(`users/invitations/${id}/feedback-done`),
      )
      .then((result) => result.data);
  }

  public async isCertified(id: number): Promise<Certification> {
    return axios
      .post<Certification>(
        this.apiCallService.apiUrl(`users/${id}/certification`),
      )
      .then((result) => result.data);
  }

  public async feedbackCompletion(id: number): Promise<InvitedUserInterface> {
    return axios
      .put(
        this.apiCallService.apiUrl(`users/invitations/${id}/end-assessment`),
        {},
      )
      .then((result) => result.data);
  }
  public async extendFeedback(invitationId: number, extendDate: string) {
    return axios.put<{
      feedback_completion_date: string;
    }>(
      this.apiCallService.apiUrl(
        `users/invitations/${invitationId}/extend-feedback`,
      ),
      { feedback_completion_date: extendDate },
    );
  }

  public async getParticiapntDashboardData(
    participantId: number,
  ): Promise<ParticipantDashboard> {
    return axios
      .get<ParticipantDashboard>(
        this.apiCallService.apiUrl(`dashboard/participants/${participantId}`),
      )
      .then((result) => result.data);
  }

  public async reopenAssessment(
    invitationId: number,
    feedbackCompletionDate: string,
  ): Promise<InvitedUserInterface> {
    return axios
      .put<InvitedUserInterface>(
        this.apiCallService.apiUrl(
          `users/invitations/${invitationId}/reopen-assessment`,
        ),
        { feedback_completion_date: feedbackCompletionDate },
      )
      .then((result) => result.data);
  }
}
